import moment from "moment";
import {
  AssessmentQuestionComment,
  AssessmentQuestionResponses,
  CompetencyAssessmentType,
  GetAssessmentGroupForSidebarQuery,
  GetReviewPreviewPersonQueryQuery,
  OrganizationTalentIndicatorFragment,
  ReviewPreviewRoleFragment,
} from "types/graphql-schema";

import { UseLabelType } from "@apps/use-label/use-label";
import { assertEdgesNonNull } from "@helpers/helpers";

import { convertEvaluationPeriodTypeToPeriod } from "../helpers";
import {
  ProgramForm,
  ProgramFormCoreValueReviewConfig,
  ProgramFormGoalReviewConfig,
  ProgramFormRoleBasedReviewConfig,
  ProgramFormTalentIndicatorReviewConfig,
} from "../types";
import { talentIndicatorTitle } from "./system-question-summaries";

// Builds the questions one reviewer would be asked, in the order the review
// shows them: the question set, then roles, goals, core values and talent
// indicators. review-preview.tsx draws them.

export enum PreviewReviewer {
  Manager = "manager",
  Self = "self",
  Peer = "peer",
}

export type PreviewInput =
  | {
      kind: "scale";
      startValue: number;
      endValue: number;
      labels: ReadonlyArray<string | null>;
      labelDescriptions: ReadonlyArray<string | null>;
    }
  | {
      kind: "choices";
      options: ReadonlyArray<string | null>;
      optionDescriptions: ReadonlyArray<string | null>;
    }
  | { kind: "text" };

export type PreviewQuestion = {
  key: string;
  // Empty for responsibilities, whose wording is all in the description.
  title: string;
  // Marks a made-up question standing in for each person's own.
  example?: boolean;
  // Plain text, or rich text JSON from the question set and talent indicators.
  description?: { plain: string } | { rich: string };
  input: PreviewInput;
  comment: AssessmentQuestionComment;
};

export type PreviewSection = {
  key: string;
  title: string;
  description?: { plain: string } | { rich: string };
  // Why a picked person has no questions here. Keeps the section showing.
  note?: string;
  questions: PreviewQuestion[];
};

export type QuestionSet = NonNullable<
  GetAssessmentGroupForSidebarQuery["assessmentGroup"]
>;

// The person picked to preview, whose roles and goals replace the examples.
export type PreviewPerson = NonNullable<
  GetReviewPreviewPersonQueryQuery["user"]
>;

type CoreValue = {
  id: number;
  title: string;
  description?: string | null;
  emoji?: string | null;
};

// The same rule the review uses to drop questions for self reviews.
const isAskedOf = (
  responses: AssessmentQuestionResponses,
  reviewer: PreviewReviewer,
) =>
  reviewer === PreviewReviewer.Self
    ? responses !== AssessmentQuestionResponses.ExcludeSelfAssessment
    : responses !== AssessmentQuestionResponses.SelfAssessmentOnly;

const configScale = (config: {
  assessmentScale: number;
  assessmentScaleStartsAt: number;
  assessmentScaleLabels: string[];
}): PreviewInput => ({
  kind: "scale",
  startValue: config.assessmentScaleStartsAt,
  endValue: config.assessmentScaleStartsAt + config.assessmentScale - 1,
  labels: config.assessmentScaleLabels,
  labelDescriptions: [],
});

const overallDescription = {
  plain: "One overall rating, shown with the average of the ratings above.",
};

const questionSetSections = (
  questionSet: QuestionSet,
  reviewer: PreviewReviewer,
): PreviewSection[] =>
  assertEdgesNonNull(questionSet.sections).map((section) => ({
    key: `set-${section.id}`,
    title: section.title ?? "",
    description: section.description
      ? { rich: section.description }
      : undefined,
    questions: assertEdgesNonNull(section.questions).flatMap(
      ({ id, question }): PreviewQuestion[] => {
        if (!isAskedOf(question.responses, reviewer)) {
          return [];
        }
        const base = {
          key: `set-${id}`,
          title: question.title,
          description: question.description
            ? { rich: question.description }
            : undefined,
          comment: question.comment,
        };
        switch (question.__typename) {
          case "RangeAssessmentQuestionNode":
            return [
              {
                ...base,
                input: {
                  kind: "scale",
                  startValue: question.startValue,
                  endValue: question.endValue,
                  labels: question.labels,
                  labelDescriptions: question.labelDescriptions,
                },
              },
            ];
          case "NpsAssessmentQuestionNode":
            return [
              {
                ...base,
                input: {
                  kind: "scale",
                  startValue: 0,
                  endValue: 10,
                  labels: [],
                  labelDescriptions: [],
                },
              },
            ];
          case "MultiChoiceAssessmentQuestionNode":
            return [
              {
                ...base,
                input: {
                  kind: "choices",
                  options: question.options,
                  optionDescriptions: question.optionDescriptions,
                },
              },
            ];
          case "TextAssessmentQuestionNode":
            return [{ ...base, input: { kind: "text" } }];
          default:
            // Question sets only hold the types above.
            return [];
        }
      },
    ),
  }));

const rolesSection = (
  config: ProgramFormRoleBasedReviewConfig,
  label: UseLabelType,
): PreviewSection => ({
  key: "roles",
  title: "Roles and Responsibilities",
  description: {
    plain: `Each ${label(
      "competency",
    )} of the person's role is its own question, like the example below.`,
  },
  questions: [
    {
      key: "roles-example",
      title: "Communication",
      example: true,
      description: {
        plain:
          "Shares clear, timely updates and adapts the message to the audience.",
      },
      input: configScale(config),
      comment: AssessmentQuestionComment.Optional,
    },
  ],
});

const goalsSection = (
  config: ProgramFormGoalReviewConfig,
  label: UseLabelType,
): PreviewSection => ({
  key: "goals",
  title: `${label("goal", { capitalize: true })} Progression`,
  description: {
    plain: `Each ${label("goal")} the person has in the ${label(
      "review",
    )} period is its own question, like the example below.`,
  },
  questions: [
    {
      key: "goals-example",
      title: "Launch the new onboarding flow",
      example: true,
      input: configScale(config),
      comment: AssessmentQuestionComment.Optional,
    },
    {
      key: "goals-overall",
      title: `${label("goal", { capitalize: true, pluralize: true })} Overall`,
      description: overallDescription,
      input: configScale(config),
      comment: AssessmentQuestionComment.Optional,
    },
  ],
});

const roleQuestions = (
  role: ReviewPreviewRoleFragment,
  competencyAssessmentType: CompetencyAssessmentType,
  input: PreviewInput,
): PreviewQuestion[] => [
  ...assertEdgesNonNull(role.competencies).flatMap((competency) =>
    competencyAssessmentType === CompetencyAssessmentType.CriteriaCombined
      ? // The review skips a competency with no criteria.
        assertEdgesNonNull(competency.criteria).length === 0
        ? []
        : [
            {
              key: `role-${role.id}-competency-${competency.id}`,
              title: competency.title,
              description: { rich: competency.description },
              input,
              comment: AssessmentQuestionComment.Optional,
            },
          ]
      : assertEdgesNonNull(competency.criteria).map((criteria) => ({
          key: `role-${role.id}-criteria-${criteria.id}`,
          title: competency.title,
          description: { rich: criteria.content },
          input,
          comment: AssessmentQuestionComment.Optional,
        })),
  ),
  ...assertEdgesNonNull(role.responsibilities).map((responsibility) => ({
    key: `role-${role.id}-responsibility-${responsibility.id}`,
    title: "",
    description: { rich: responsibility.content },
    input,
    comment: AssessmentQuestionComment.Optional,
  })),
];

// One section per current role, and one for the next role when it is assessed,
// as the review builds them.
const personRolesSections = (
  person: PreviewPerson,
  config: ProgramFormRoleBasedReviewConfig,
  reviewer: PreviewReviewer,
): PreviewSection[] => {
  const input = configScale(config);
  if (person.currentRoles.length === 0) {
    return [
      {
        key: "roles",
        title: "Roles and Responsibilities",
        note: `${person.name} has no role, so there are no role questions.`,
        questions: [],
      },
    ];
  }
  const currentRoleIds = person.currentRoles.map(({ id }) => id);
  const assessesNextRole =
    reviewer === PreviewReviewer.Self
      ? config.enabledSelfReviewForNextRole
      : reviewer === PreviewReviewer.Manager && config.enabledForNextRole;
  // Two current roles can lead to the same next role. It is asked once.
  const askedNextRoleIds = new Set<number>();
  return person.currentRoles.flatMap((role) => {
    const { nextRole } = role;
    const asksNextRole =
      assessesNextRole &&
      !!nextRole &&
      !currentRoleIds.includes(nextRole.id) &&
      !askedNextRoleIds.has(nextRole.id);
    if (asksNextRole) {
      askedNextRoleIds.add(nextRole.id);
    }
    return [
      {
        key: `role-${role.id}`,
        title: `Roles and Responsibilities: ${role.title}`,
        questions: roleQuestions(role, config.competencyAssessmentType, input),
      },
      ...(asksNextRole
        ? [
            {
              key: `next-role-${nextRole.id}`,
              title: `Next Role: ${nextRole.title}`,
              questions: roleQuestions(
                nextRole,
                config.nextRoleCompetencyAssessmentType,
                input,
              ),
            },
          ]
        : []),
    ];
  });
};

const personGoalsSection = (
  person: PreviewPerson,
  config: ProgramFormGoalReviewConfig,
  [periodStart, periodEnd]: [string, string],
  label: UseLabelType,
): PreviewSection => {
  // Due in the period, as the review asks. Dates compare as YYYY-MM-DD text.
  const goals = (
    person.ownedGoals ? assertEdgesNonNull(person.ownedGoals) : []
  ).filter(
    (goal) =>
      "dueDate" in goal &&
      !!goal.dueDate &&
      goal.dueDate >= periodStart &&
      goal.dueDate <= periodEnd,
  );
  return {
    key: "goals",
    title: `${label("goal", { capitalize: true })} Progression`,
    note:
      goals.length === 0
        ? `${person.name} has no ${label("goal", {
            pluralize: true,
          })} due in the ${label("review")} period.`
        : undefined,
    questions: [
      ...goals.map((goal) => ({
        key: `goal-${goal.id}`,
        title: goal.title ?? "",
        input: configScale(config),
        comment: AssessmentQuestionComment.Optional,
      })),
      {
        key: "goals-overall",
        title: `${label("goal", { capitalize: true, pluralize: true })} Overall`,
        description: overallDescription,
        input: configScale(config),
        comment: AssessmentQuestionComment.Optional,
      },
    ],
  };
};

// The dates a goal must be due between to be asked about. Ongoing reviews
// count each period in days from the person's start date, as the review
// does. Without a period, the review uses the current quarter.
export const previewGoalPeriod = (
  program: Pick<
    ProgramForm,
    | "ongoing"
    | "evaluationPeriod"
    | "startDate"
    | "startDatePeriodStartOffset"
    | "startDatePeriodEndOffset"
  >,
  personStartDate?: string | null,
  today = moment(),
): [string, string] => {
  const { periodStartDate, periodEndDate } = program.ongoing
    ? ongoingPeriod(program, personStartDate)
    : convertEvaluationPeriodTypeToPeriod(
        program.evaluationPeriod,
        program.startDate,
      );
  return periodStartDate && periodEndDate
    ? [periodStartDate, periodEndDate]
    : [
        today.clone().startOf("quarter").format("YYYY-MM-DD"),
        today.clone().endOf("quarter").format("YYYY-MM-DD"),
      ];
};

const ongoingPeriod = (
  {
    startDatePeriodStartOffset: startOffset,
    startDatePeriodEndOffset: endOffset,
  }: Pick<
    ProgramForm,
    "startDatePeriodStartOffset" | "startDatePeriodEndOffset"
  >,
  personStartDate?: string | null,
): { periodStartDate?: string; periodEndDate?: string } =>
  personStartDate && startOffset != null && endOffset != null
    ? {
        periodStartDate: moment(personStartDate)
          .add(startOffset, "days")
          .format("YYYY-MM-DD"),
        periodEndDate: moment(personStartDate)
          .add(endOffset, "days")
          .format("YYYY-MM-DD"),
      }
    : {};

const coreValuesSection = (
  config: ProgramFormCoreValueReviewConfig,
  coreValues: CoreValue[],
): PreviewSection => ({
  key: "core-values",
  title: "Core Values",
  questions: [
    ...coreValues.map((coreValue) => ({
      key: `core-value-${coreValue.id}`,
      title: `${coreValue.emoji ? `${coreValue.emoji} ` : ""}${
        coreValue.title
      }`,
      description: coreValue.description
        ? { plain: coreValue.description }
        : undefined,
      input: configScale(config),
      comment: AssessmentQuestionComment.Optional,
    })),
    {
      key: "core-values-overall",
      title: "Core Values Overall",
      description: overallDescription,
      input: configScale(config),
      comment: AssessmentQuestionComment.Optional,
    },
  ],
});

const talentIndicatorsSection = (
  config: ProgramFormTalentIndicatorReviewConfig,
  talentIndicators: OrganizationTalentIndicatorFragment[],
  reviewer: PreviewReviewer,
): PreviewSection => ({
  key: "talent-indicators",
  title: config.sectionTitle || "Talent Indicators",
  description: config.sectionDescription
    ? { rich: config.sectionDescription }
    : undefined,
  questions: config.talentIndicators.flatMap((setting): PreviewQuestion[] => {
    const talentIndicator = talentIndicators.find(
      ({ id }) => id === setting.talentIndicatorId,
    );
    if (!talentIndicator || !isAskedOf(setting.responses, reviewer)) {
      return [];
    }
    return [
      {
        key: `talent-indicator-${talentIndicator.id}`,
        title: talentIndicatorTitle(talentIndicator),
        description: setting.description
          ? { rich: setting.description }
          : undefined,
        input: {
          kind: "scale",
          startValue: talentIndicator.scaleStartsAt,
          endValue: talentIndicator.scaleStartsAt + talentIndicator.scale - 1,
          labels: talentIndicator.labels,
          labelDescriptions: talentIndicator.labelDescriptions,
        },
        comment: setting.comment,
      },
    ];
  }),
});

export const buildPreviewSections = ({
  reviewer,
  questionSet,
  roleBasedReviewConfig,
  goalReviewConfig,
  coreValueReviewConfig,
  talentIndicatorReviewConfig,
  talentIndicators,
  coreValues,
  hasGoals,
  person,
  goalPeriod,
  label,
}: {
  reviewer: PreviewReviewer;
  questionSet?: QuestionSet | null;
  roleBasedReviewConfig: ProgramFormRoleBasedReviewConfig;
  goalReviewConfig: ProgramFormGoalReviewConfig;
  coreValueReviewConfig: ProgramFormCoreValueReviewConfig;
  // Only self and manager reviews have talent indicators.
  talentIndicatorReviewConfig?: ProgramFormTalentIndicatorReviewConfig;
  talentIndicators: OrganizationTalentIndicatorFragment[];
  coreValues: CoreValue[];
  // Whether the organization uses goals at all.
  hasGoals: boolean;
  person?: PreviewPerson | null;
  // When a goal must be due to be asked about. See previewGoalPeriod.
  goalPeriod: [string, string];
  label: UseLabelType;
}): PreviewSection[] => {
  const sections = questionSet
    ? questionSetSections(questionSet, reviewer)
    : [];
  if (roleBasedReviewConfig.enabledForCurrentRole) {
    sections.push(
      ...(person
        ? personRolesSections(person, roleBasedReviewConfig, reviewer)
        : [rolesSection(roleBasedReviewConfig, label)]),
    );
  }
  if (
    hasGoals &&
    goalReviewConfig.enabled &&
    isAskedOf(goalReviewConfig.responses, reviewer)
  ) {
    sections.push(
      person
        ? personGoalsSection(person, goalReviewConfig, goalPeriod, label)
        : goalsSection(goalReviewConfig, label),
    );
  }
  if (
    coreValues.length > 0 &&
    coreValueReviewConfig.enabled &&
    isAskedOf(coreValueReviewConfig.responses, reviewer)
  ) {
    sections.push(coreValuesSection(coreValueReviewConfig, coreValues));
  }
  if (talentIndicatorReviewConfig) {
    sections.push(
      talentIndicatorsSection(
        talentIndicatorReviewConfig,
        talentIndicators,
        reviewer,
      ),
    );
  }
  return sections.filter(({ questions, note }) => questions.length > 0 || note);
};
